import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { CustomTextFieldInput, MultiSelectInput } from "_components";
import { alertActions } from "_store";
import { Typography, Button, Box } from "@mui/material";
import Popper from "@mui/material/Popper";
import Grid from "@material-ui/core/Grid";
import FilterListIcon from "@mui/icons-material/FilterList";
import { activityFilterSchema } from "_utils/validationSchema";
import dayjs from "dayjs";
import {
  getSupplierDiversityCategoryApi,
  getSupplierDiversityStateApi,
  postExpiringSupplierApi,
  postRegisteredSupplierFilterApi,
} from "services/supplierDiversity/supplierDiversity.thunks";
import { ERR_MSG } from "constants/labels";
import CustomStaticDateRangePicker3 from "_components/CustomStaticDateRangePicker3";

export const Filter = ({
  handleFilterSubmit,
  isOpen,
  onClose,
  onOpen,
  activeView,
  expiringInData,
  setDownloadPayload,
}) => {
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);

  const [loading, setLoading] = useState(false);

  const { handleSubmit, control, watch, reset, trigger, setValue } = useForm({    
    defaultValues: {
      SelectedDate: [null, null],
      Status: "",
      Category: "",
      State: "",
      ExpiringIn: "",
    },
  });
  const [categoryData, setCategoryData] = useState([]);
  const [stateData, setStateData] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedExpiringIn, setSelectedExpiringIn] = useState("");

  const watchedValues = watch();
  const today = dayjs().endOf("day").toDate();

  const selectedRange = watchedValues.SelectedDate || [null, null];
  const fetchSupplierState = async () => {
    setLoading(true);
    try {
      const resultState = await dispatch(
        getSupplierDiversityStateApi()
      ).unwrap();
      const options = (resultState?.Data ?? []).map((c) => ({
        label: c.StateDescription,
        value: c.StateId,
      }));
      setStateData(options);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: "Supplier Development",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchSupplierCategory = async () => {
    setLoading(true);
    try {
      const resultCat = await dispatch(
        getSupplierDiversityCategoryApi()
      ).unwrap();
      const options = (resultCat?.Data ?? []).map((c) => ({
        label: c.CategoryName,
        value: c.CategoryID,
      }));
      setCategoryData(options);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: "Supplier Development",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSupplierCategory();
    fetchSupplierState();
  }, []);

  useEffect(() => {
    const hasValue = Object.values(watchedValues).some(
      (value) => value !== "" && value !== null
    );
    setIsFormValid(hasValue);
  }, [watchedValues]);

  const canBeOpen = isOpen && Boolean(anchorEl);
  const id = canBeOpen ? "simple-popper" : undefined;

  const onSubmit = async (formData) => {
    dispatch(alertActions.clear());

    try {
      const [startDate, endDate] = formData.SelectedDate || [null, null];
      const RegisteredFrom = startDate
        ? dayjs(startDate).format("YYYY-MM-DDTHH:mm:ss")
        : null;
      const RegisteredTo = endDate
        ? dayjs(endDate).format("YYYY-MM-DDTHH:mm:ss")
        : null;

      const stateArray = formData.State ? formData.State.split(",") : null;
      const categoryArray = formData.Category
        ? formData.Category.split(",")
        : null;
      const expiringInArray = formData.ExpiringIn
        ? formData.ExpiringIn.split(",")
        : null;

      const payload = {
        RegisteredFrom,
        RegisteredTo,
        CompanyName: formData.CompanyName || null,
        StateId: stateArray,
        CategoryId: categoryArray,
        ExpiringIn: activeView === "expiringIN" ? expiringInArray : null,
      };

      const apiThunk =
        activeView === "expiringIN"
          ? postExpiringSupplierApi
          : activeView === "newSuppliers"
          ? postRegisteredSupplierFilterApi
          : postRegisteredSupplierFilterApi;

      const result = await dispatch(apiThunk(payload)).unwrap();
      setDownloadPayload(payload);

      const { Data, Succeeded, Message } = result;

      handleFilterSubmit(Succeeded ? Data : []);
      if (!Succeeded)
        dispatch(
          alertActions.error({
            message: Message,
            header: "Supplier Development",
          })
        );

      resetValues();
      onClose();
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: "Supplier Development",
        })
      );
    }
  };

  const handleBlur = async (e) => {
    const fieldName = e.target.name;
    await trigger(fieldName);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    if (isOpen) {
      onClose();
    } else {
      onOpen();
    }
  };

  const handleCancelClick = () => {
    resetValues();
    onClose();
  };

  const resetValues = () => {
    reset({
      SelectedDate: [null, null],
      UserEmail: "",
      CompanyName: "",
      State: "",
      Category: "",
      ExpiringIn: "",
    });
    setSelectedState("");
    setSelectedCategory("");
    setSelectedExpiringIn("");
  };

  const handleStateChange = (newValue) => {
    setSelectedState(newValue);
    setValue("State", newValue);
    trigger("State");
  };

  const handleCategoryChange = (newValue) => {
    setSelectedCategory(newValue);
    setValue("Category", newValue);
    trigger("Category");
  };

  const handleExpiringInChange = (newValue) => {
    setSelectedExpiringIn(newValue);
    setValue("ExpiringIn", newValue);
    trigger("ExpiringIn");
  };

  const expiringInOptions = expiringInData.map((value) => ({
    label: `${value}`,
    value: value,
  }));

  return (
    <>
      <Button
        className="Filter"
        type="button"
        variant="contained"
        color="primary"
        aria-describedby={id}
        onClick={handleClick}
      >
        <FilterListIcon />
        Filter
      </Button>
      <Popper
        id={id}
        open={canBeOpen}
        anchorEl={anchorEl}
        className="Filtercontainer"
      >
        <Box
          sx={{ border: 1, p: 1, bgcolor: "background.paper" }}
          className="Filtercontainerinner Border"
        >
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="Registrationcontainer marketerFiltercontainer"
          >
            <Typography component="div" className="userprofilelist">
              <Grid container direction="row" spacing={{ xs: 2, md: 3 }}>
                <Grid item xs={12} sm={12} md={12}>
                  <Typography variant="h2" className="userprofilelistcontent">
                    Supplier Development
                  </Typography>
                </Grid>
              </Grid>
            </Typography>
            <Typography
              component="div"
              className="passwordcheck SelectedDateSD border-none  selecticon"
              sx={{ position: "relative", zIndex: 10 }}
            >
              <CustomStaticDateRangePicker3
                control={control}
                trigger={trigger}
                name="SelectedDate"
                label="From - To"
                minDate={dayjs().subtract(12, "month").toDate()}
                maximumDate={today}
                startDate={selectedRange[0]}
                endDate={selectedRange[1]}
                onDateRangeChange={({ startDate, endDate }) => {
                  setValue("SelectedDate", [
                    dayjs(startDate).toDate(),
                    dayjs(endDate).toDate(),
                  ]);

                  if (handleBlur) {
                    handleBlur({ target: { name: "SelectedDate" } });
                  }
                }}
              />
            </Typography>

            <CustomTextFieldInput
              control={control}
              name="CompanyName"
              label="Company Name"
            />

            <MultiSelectInput
              id="state"
              control={control}
              name="State"
              label="State"
              options={stateData}
              value={selectedState}
              onChange={handleStateChange}
              handleBlur={handleBlur}
              setValue={setValue}
              trigger={trigger}
            />

            <MultiSelectInput
              id="category"
              control={control}
              name="Category"
              label="Category"
              options={categoryData}
              value={selectedCategory}
              onChange={handleCategoryChange}
              handleBlur={handleBlur}
              setValue={setValue}
              trigger={trigger}
              loading={loading}
            />

            {activeView === "expiringIN" && (
              <MultiSelectInput
                id="expiringIn"
                control={control}
                name="ExpiringIn"
                label="Expiring in"
                options={expiringInOptions}
                value={selectedExpiringIn}
                onChange={handleExpiringInChange}
                handleBlur={handleBlur}
                setValue={setValue}
                trigger={trigger}
              />
            )}

            <Typography component="div" className="CreateMarketerbutton">
              <Button
                type="submit"
                variant="contained"
                className="submitbutton"
                color="primary"
                disabled={!isFormValid}
              >
                Search
              </Button>
              <Button
                variant="contained"
                color="red"
                className="cancelbutton"
                onClick={handleCancelClick}
              >
                Cancel
              </Button>
            </Typography>
          </form>
        </Box>
      </Popper>
    </>
  );
};
